// ── shareReview ───────────────────────────────────────────────────────────────
// "Share this review" — the on-brand alternative to a row of social buttons. Renders a
// branded review card (canvas → PNG) and hands it to the OS share sheet via the Web
// Share API (which already surfaces Messages/Twitter/whatever the person actually uses),
// with a copy-link fallback when sharing isn't available. The image doubles as marketing:
// it carries the NextEp mark + a link back.

import { drawQR } from './drawQR'
import { withShareRef } from './refTag'

const APP_ORIGIN = 'https://app.nextep.show'

// Strip the markdown-lite markers so the shared card reads as clean prose.
function plain(body) {
  return String(body || '').replace(/\*\*|\|\||\*/g, '').trim()
}

function wrapLines(ctx, text, maxWidth, maxLines) {
  const out = []
  for (const para of text.split('\n')) {
    if (out.length >= maxLines) break
    let line = ''
    for (const word of para.split(/\s+/)) {
      const test = line ? line + ' ' + word : word
      if (ctx.measureText(test).width > maxWidth && line) {
        out.push(line); line = word
        if (out.length >= maxLines) break
      } else line = test
    }
    if (out.length < maxLines && line) out.push(line)
  }
  if (out.length === maxLines) {
    let last = out[maxLines - 1]
    while (last && ctx.measureText(last + '…').width > maxWidth) last = last.slice(0, -1)
    out[maxLines - 1] = last + '…'
  }
  return out
}

// Draw a 1080×1350 (4:5) portrait share card and return a PNG blob (or null on failure).
async function makeReviewCard({ showName, author, rating, body, url }) {
  try {
    if (document.fonts?.ready) { try { await document.fonts.ready } catch { /* proceed */ } }
    const W = 1080, H = 1350, PAD = 88
    const c = document.createElement('canvas'); c.width = W; c.height = H
    const ctx = c.getContext('2d'); if (!ctx) return null

    ctx.fillStyle = '#07070f'; ctx.fillRect(0, 0, W, H)
    // Soft amber glow top-left
    const g = ctx.createRadialGradient(220, 180, 40, 220, 180, 900)
    g.addColorStop(0, 'rgba(245,200,66,0.14)'); g.addColorStop(1, 'rgba(245,200,66,0)')
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H)
    // Border
    ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 2
    ctx.strokeRect(24, 24, W - 48, H - 48)

    // Wordmark
    ctx.fillStyle = '#f5c842'
    ctx.font = '800 30px Outfit, sans-serif'
    ctx.textBaseline = 'alphabetic'
    ctx.fillText('N E X T E P', PAD, 150)
    ctx.fillStyle = 'rgba(231,231,255,0.55)'
    ctx.font = '600 22px Outfit, sans-serif'
    ctx.fillText('A REVIEW', PAD, 186)

    // Show title (wrapped, up to 2 lines)
    ctx.fillStyle = '#ffffff'
    ctx.font = '800 62px Outfit, sans-serif'
    const titleLines = wrapLines(ctx, showName || 'A show', W - PAD * 2, 2)
    let y = 300
    for (const l of titleLines) { ctx.fillText(l, PAD, y); y += 74 }

    // Rating
    if (rating > 0) {
      ctx.fillStyle = '#f5c842'
      ctx.font = '700 40px Outfit, sans-serif'
      ctx.fillText(`🎬 ${Number.isInteger(rating) ? rating : rating.toFixed(1)} / 5`, PAD, y + 20)
      y += 70
    }

    // Divider
    y += 24
    ctx.strokeStyle = 'rgba(245,200,66,0.35)'; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(PAD + 120, y); ctx.stroke()
    y += 60

    // Review body
    ctx.fillStyle = 'rgba(231,231,255,0.92)'
    ctx.font = '400 40px Outfit, sans-serif'
    const bodyLines = wrapLines(ctx, plain(body), W - PAD * 2, 12)
    for (const l of bodyLines) { ctx.fillText(l, PAD, y); y += 58 }

    // Footer
    ctx.fillStyle = 'rgba(231,231,255,0.62)'
    ctx.font = '600 26px Outfit, sans-serif'
    ctx.fillText(`— ${author || 'A viewer'}`, PAD, H - 150)
    ctx.fillStyle = '#f5c842'
    ctx.font = '700 26px Outfit, sans-serif'
    ctx.fillText('app.nextep.show', PAD, H - 100)

    // Scan-to-open QR (bottom-right) → the review's show/rating page.
    drawQR(ctx, url || APP_ORIGIN, W - PAD - 150, H - PAD - 150, 150)

    return await new Promise(res => c.toBlob(b => res(b), 'image/png'))
  } catch { return null }
}

// Share a review. Returns 'shared' | 'copied' | 'cancel' | 'fail'.
export async function shareReview({ showId, showName, author, rating = 0, body }) {
  // Link via /share/v2 when there's a rating, so the unfurl shows "★ rating" instead
  // of just the generic show card (a per-review URL doesn't exist yet). Falls back to
  // the show page otherwise.
  const ratingCode = Math.round((rating || 0) * 10)
  const slug = (showName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'show'
  const url = withShareRef(showId
    ? (ratingCode ? `${APP_ORIGIN}/share/v2/${showId}-${ratingCode}-${slug}` : `${APP_ORIGIN}/show/${showId}`)
    : APP_ORIGIN, 'review-card')
  const text = `${author ? author + "'s" : 'A'} review of ${showName || 'a show'} on NextEp`

  let file = null
  const blob = await makeReviewCard({ showName, author, rating, body, url })
  if (blob) { try { file = new File([blob], 'nextep-review.png', { type: 'image/png' }) } catch { file = null } }

  // Prefer sharing the image + link (native sheet → any app the person uses).
  if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ title: 'NextEp', text, url, files: [file] }); return 'shared' }
    catch (e) { if (e?.name === 'AbortError') return 'cancel' }
  }
  if (navigator.share) {
    try { await navigator.share({ title: 'NextEp', text, url }); return 'shared' }
    catch (e) { if (e?.name === 'AbortError') return 'cancel' }
  }
  // No share sheet (most desktops) → copy the link.
  try { await navigator.clipboard.writeText(`${text}\n${url}`); return 'copied' }
  catch { return 'fail' }
}
